import { Box, Typography, useTheme } from '@mui/material';
import { History, ShieldCheck, Zap } from 'lucide-react';
import { useState } from 'react';
import { tokens } from '../../theme';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import { Button } from '../../components/Button';
import ModalCompleteRegister from './ModalCompleteRegister';
import { useTranslation } from '../../hooks/useTranslation';

const STEPS = [
  {
    icon: <Zap size={22} />,
    title: 'onboarding.steps.step1.title',
    description: 'onboarding.steps.step1.description',
  },
  {
    icon: <History size={22} />,
    title: 'onboarding.steps.step2.title',
    description: 'onboarding.steps.step2.description',
  },
  {
    icon: <ShieldCheck size={22} />,
    title: 'onboarding.steps.step3.title',
    description: 'onboarding.steps.step3.description',
  },
];

const OnboardingOverlay = ({ onNextStep, onSkip }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <ModalCompleteRegister
        open={modalOpen}
        isRequestAccess
        showSkipStep
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          setModalOpen(false);
          onNextStep?.();
        }}
        onNextStep={onNextStep}
        onSkip={() => {
          setModalOpen(false);
          onSkip?.();
        }}
      />

      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(4px)',
          px: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            p: { xs: '32px 24px', sm: '48px 56px' },
            maxWidth: 560,
            width: '100%',
            boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px',
            animation: 'fadeSlideUp 0.35s ease',
            '@keyframes fadeSlideUp': {
              from: { opacity: 0, transform: 'translateY(24px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: `${colors.greenAccent[500]}22`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.greenAccent[400],
            }}
          >
            <WavingHandIcon fontSize="large" />
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" fontWeight={700} sx={{ mb: '8px', color: colors.grey[100] }}>
              {t('onboarding.welcomeMessage')}
            </Typography>
            <Typography variant="body1" sx={{ color: colors.grey[300], lineHeight: 1.6 }}>
              <Box component="span" sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
                {t('onboarding.welcomeSubtitle')}
              </Box>
              <br />
              {t('onboarding.startSubtitle')}
            </Typography>
          </Box>

          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {STEPS.map((step, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  backgroundColor: colors.grey[900],
                  borderRadius: '10px',
                  p: '14px 18px',
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    backgroundColor: `${colors.greenAccent[500]}22`,
                    color: colors.greenAccent[400],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {step.icon}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: colors.grey[100] }}>
                    {t(step.title)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                    {t(step.description)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <Button
              variant="contained"
              fullWidth={false}
              onClick={() => setModalOpen(true)}
              sx={{
                fontWeight: 500,
                fontSize: '18px',
              }}
            >
              {t('onboarding.startNow')}
            </Button>
            <Typography
              variant="caption"
              sx={{ color: colors.grey[500], mt: '10px', display: 'block' }}
            >
              {t('onboarding.takeOneMinute')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default OnboardingOverlay;
